/**
 * Yapısal motor: bölüm bütünlüğü, tablo/şekil referansları.
 *
 * - IMRaD bölümlerinin varlığı ve sırası
 * - Tablo/şekil altyazıları ile metin içi referansların çapraz kontrolü
 * - Numaralandırma boşlukları (Tablo 1, Tablo 3 var; 2 yok)
 * - Özette anahtar kelime eksikliği
 *
 * Sayfa/bbox olmadığında yalnızca metin offset'i kullanılır; bu, yapısal
 * bulguların güvenini düşürür.
 */

import type {
	CaptionRef,
	Confidence,
	DetectedSection,
	InTextRef,
	Issue,
	SectionKey,
	StructureResult
} from '../types';
import type { DocumentIndex } from '../text/document';
import { excerpt, foldTurkish } from '../text/normalize';

export interface StructureEngineOutput {
	result: StructureResult;
	issues: Issue[];
}

const SCALE_RE =
	/(?:ölçe[kğ]|anket|envanter|inventory|questionnaire|(?<![\w-])scale(?![\w-])(?!\s+bar)|survey\s+instrument|likert|item\s+pool|madde\s+havuzu|test\s+battery)/giu;
const RELIABILITY_RE =
	/(?:Cronbach['’]?s?\s*(?:alpha|α|alfa)|McDonald['’]?s?\s*(?:omega|ω)|güvenirlik\s+katsayısı|reliability\s+coefficient|internal\s+consistency|iç\s+tutarlılık|test-retest|split-half|yarıya\s+bölme|\bICC\b|(?:α|alpha)\s*=\s*[0-9.,]+|KR-?20|Kuder-Richardson|composite\s+reliability|\bCR\b\s*=\s*0?\.[0-9])/iu;
const VALIDITY_RE =
	/(?:geçerli[kl]|validity|factor\s+analysis|faktör\s+analizi|CFA|EFA|confirmatory|exploratory|açımlayıcı|doğrulayıcı|AVE|convergent|discriminant|yapı\s+geçerliği|construct\s+validity)/iu;

interface SectionSpec {
	key: SectionKey;
	/** Düşük harfe çevrilmiş, diakritikleri sadeleştirilmiş başlık kalıpları. */
	patterns: RegExp[];
	required: boolean;
	label: string;
}

const SECTION_SPECS: SectionSpec[] = [
	{
		key: 'abstract',
		patterns: [/^(?:oz|ozet|abstract|summary)\b/],
		required: true,
		label: 'Öz / Abstract'
	},
	{
		key: 'keywords',
		patterns: [/^(?:anahtar\s*kelime(?:ler)?|anahtar\s*sozcuk(?:ler)?|keywords?|key\s*words?)\b/],
		required: false,
		label: 'Anahtar kelimeler'
	},
	{
		key: 'introduction',
		patterns: [/^(?:giris|introduction|amac|amac\s*ve\s*kapsam)\b/],
		required: true,
		label: 'Giriş'
	},
	{
		key: 'method',
		patterns: [
			/^(?:yontem|yontemler|materyal\s*ve\s*yontem|materyal\s*ve\s*metot|gerec\s*ve\s*yontem|metodoloji|method|methods|methodology|participants|katilimcilar)\b/
		],
		required: true,
		label: 'Yöntem'
	},
	{
		key: 'results',
		patterns: [/^(?:bulgular|sonuclar|results|findings)\b/],
		required: true,
		label: 'Bulgular'
	},
	{
		key: 'discussion',
		patterns: [/^(?:tartisma|sonuc\s*ve\s*tartisma|tartisma\s*ve\s*sonuc|discussion|conclusion)\b/],
		required: true,
		label: 'Tartışma'
	},
	{
		key: 'references',
		patterns: [/^(?:kaynakca|kaynaklar|referanslar|bibliyografya|references|bibliography)\b/],
		required: false,
		label: 'Kaynakça'
	},
	{
		key: 'appendix',
		patterns: [/^(?:ek\b|ekler|appendix|supplementary)\b/],
		required: false,
		label: 'Ekler'
	}
];

/** Başlık satırı gibi görünen satır: kısa, büyük harfe yakın, numaralı olabilir. */
function looksLikeHeading(line: string): boolean {
	const t = line.trim();
	if (t.length === 0 || t.length > 80) return false;
	if (/[.!?]$/.test(t) && t.split(/\s+/).length > 6) return false;
	const words = t.split(/\s+/);
	if (words.length > 10) return false;
	return true;
}

export function runStructureEngine(index: DocumentIndex): StructureEngineOutput {
	const text = index.text;
	const lines = text.split('\n');
	const issues: Issue[] = [];

	/* 1) Bölüm tespiti ---------------------------------------------------- */

	const sections: DetectedSection[] = [];
	let cursor = 0;

	for (let i = 0; i < lines.length; i++) {
		const rawLine = lines[i];
		const lineStart = cursor;
		cursor += rawLine.length + 1;

		const trimmed = rawLine.trim();
		if (!trimmed || trimmed.length < 2) continue;

		// Numaralı başlık: "2. Yöntem", "3.1. Örneklem"
		const numbered = /^(?:\d+(?:\.\d+)*[.)]?\s+)(.+)$/.exec(trimmed);
		const candidate = numbered ? numbered[1] : trimmed;
		if (!looksLikeHeading(candidate)) continue;

		const folded = foldTurkish(candidate)
			.toLowerCase()
			.replace(/\s+/g, ' ')
			.trim();

		for (const spec of SECTION_SPECS) {
			if (!spec.patterns.some((p) => p.test(folded))) continue;
			// Aynı bölümü iki kez ekleme
			if (sections.some((s) => s.key === spec.key)) break;

			const confidence = headingConfidence(trimmed, candidate, i === 0);
			const location = index.locationAt(lineStart, lineStart + rawLine.length);
			sections.push({
				key: spec.key,
				heading: candidate.trim(),
				line: i + 1,
				location,
				confidence,
				order: sections.length
			});
			break;
		}
	}

	const foundKeys = new Set(sections.map((s) => s.key));
	const missingSections = SECTION_SPECS.filter((s) => s.required && !foundKeys.has(s.key)).map(
		(s) => s.key
	);

	for (const key of missingSections) {
		const spec = SECTION_SPECS.find((s) => s.key === key)!;
		issues.push({
			id: `struct-missing-${issues.length}`,
			code: 'struct.missing_section',
			category: 'structure',
			severity: key === 'method' || key === 'results' ? 'critical' : 'warning',
			title: `Bölüm eksik: ${spec.label}`,
			detail: `Metinde "${spec.label}" bölümü tespit edilemedi. IMRaD yapısı için bu bölüm beklenir.`,
			suggestion: `"${spec.label}" başlığını açıkça ekleyin (ör. "${spec.label}" satırı).`,
			location: {},
			confidence: 'medium',
			decisive: true
		});
	}

	// Bölüm sırası: yöntem bulgulardan önce, bulgular tartışmadan önce
	const order = (k: SectionKey) => sections.find((s) => s.key === k)?.order ?? -1;
	if (order('results') >= 0 && order('method') >= 0 && order('results') < order('method')) {
		issues.push({
			id: `struct-order-${issues.length}`,
			code: 'struct.section_order',
			category: 'structure',
			severity: 'warning',
			title: 'Bölüm sırası beklenenden farklı',
			detail: 'Bulgular bölümü Yöntem bölümünden önce geliyor.',
			suggestion: 'IMRaD sırası: Giriş → Yöntem → Bulgular → Tartışma.',
			location: {},
			confidence: 'medium',
			decisive: true
		});
	}

	/* 2) Tablo ve şekil altyazıları --------------------------------------- */

	const tables = findCaptions(text, 'table', index);
	const figures = findCaptions(text, 'figure', index);

	/* 3) Metin içi referanslar ------------------------------------------- */

	const tableRefs = findInTextRefs(text, 'table', index);
	const figureRefs = findInTextRefs(text, 'figure', index);

	/* 4) Çapraz kontrol ---------------------------------------------------- */

	const unreferencedTables = tables.filter(
		(t) => t.number === undefined || !tableRefs.some((r) => r.number === t.number)
	);
	const unreferencedFigures = figures.filter(
		(f) => f.number === undefined || !figureRefs.some((r) => r.number === f.number)
	);

	for (const t of unreferencedTables) {
		issues.push({
			id: `struct-fig-${issues.length}`,
			code: 'struct.table_unreferenced',
			category: 'structure',
			severity: 'warning',
			title: 'Tabloya metinde atıf verilmemiş',
			detail: `"${excerpt(t.raw, 100)}" altyazısı var ancak metinde "Tablo ${t.number}" ifadesine rastlanmadı.`,
			quote: excerpt(t.raw, 180),
			suggestion: `Metinde ilgili yerde "Tablo ${t.number}" şeklinde atıf verin.`,
			location: t.location,
			confidence: t.confidence,
			decisive: t.confidence !== 'low',
			meta: { number: t.number, caption: t.raw }
		});
	}

	for (const f of unreferencedFigures) {
		issues.push({
			id: `struct-fig-${issues.length}`,
			code: 'struct.figure_unreferenced',
			category: 'structure',
			severity: 'warning',
			title: 'Şekle metinde atıf verilmemiş',
			detail: `"${excerpt(f.raw, 100)}" altyazısı var ancak metinde "Şekil ${f.number}" ifadesine rastlanmadı.`,
			quote: excerpt(f.raw, 180),
			suggestion: `Metinde ilgili yerde "Şekil ${f.number}" şeklinde atıf verin.`,
			location: f.location,
			confidence: f.confidence,
			decisive: f.confidence !== 'low',
			meta: { number: f.number, caption: f.raw }
		});
	}

	// Metinde atıf var ama altyazı yok
	const danglingRefs: InTextRef[] = [];
	for (const ref of [...tableRefs, ...figureRefs]) {
		const pool = ref.type === 'table' ? tables : figures;
		if (ref.number === undefined) continue;
		if (pool.some((c) => c.number === ref.number)) continue;
		// "Tablo 1"den sonra altyazı gelmemiş olabilir; yalnızca hiç altyazı yoksa uyar
		if (pool.length === 0) continue;
		danglingRefs.push(ref);
	}

	for (const ref of danglingRefs) {
		issues.push({
			id: `struct-dangling-${issues.length}`,
			code: 'struct.ref_without_caption',
			category: 'structure',
			severity: 'warning',
			title: 'Metinde atıf var, altyazı bulunamadı',
			detail: `"${ref.raw}" ifadesi metinde geçiyor ancak ilgili ${ref.type === 'table' ? 'tablo' : 'şekil'} altyazısı bulunamadı.`,
			quote: ref.location.line ?? ref.raw,
			suggestion: 'Tabloya/şekle numaralı bir altyazı ekleyin.',
			location: ref.location,
			confidence: 'medium',
			decisive: true,
			meta: { raw: ref.raw, refType: ref.type }
		});
	}

	// Numaralandırma boşlukları
	const numberingGaps: string[] = [];
	for (const [type, pool] of [
		['Tablo', tables],
		['Şekil', figures]
	] as const) {
		const nums = [...new Set(pool.map((p) => p.number).filter((n): n is number => n !== undefined))].sort(
			(a, b) => a - b
		);
		if (nums.length < 2) continue;
		for (let n = 1; n <= nums[nums.length - 1]; n++) {
			if (!nums.includes(n)) numberingGaps.push(`${type} ${n}`);
		}
	}

	if (numberingGaps.length > 0) {
		issues.push({
			id: `struct-gap-${issues.length}`,
			code: 'struct.figure_numbering',
			category: 'structure',
			severity: 'info',
			title: 'Numaralandırmada boşluk',
			detail: `Şu numaralar eksik görünüyor: ${numberingGaps.join(', ')}.`,
			suggestion: 'Tablo/şekil numaralarını ardışık hale getirin.',
			location: {},
			confidence: 'medium',
			decisive: false,
			meta: { gaps: numberingGaps.join(', ') }
		});
	}

	// Anahtar kelime
	if (foundKeys.has('abstract') && !foundKeys.has('keywords')) {
		issues.push({
			id: `struct-keywords-${issues.length}`,
			code: 'struct.keywords_missing',
			category: 'structure',
			severity: 'info',
			title: 'Anahtar kelime satırı bulunamadı',
			detail: 'Öz bölümü var ancak "Anahtar kelimeler" satırı tespit edilemedi.',
			suggestion: 'Özün altına 3-6 anahtar kelime ekleyin.',
			location: {},
			confidence: 'medium',
			decisive: true
		});
	}

	/* Psikometrik bilgi ---------------------------------------------------- */

	const scaleMatches = [...text.matchAll(SCALE_RE)];
	if (scaleMatches.length >= 2) {
		const hasReliability = RELIABILITY_RE.test(text);
		const hasValidity = VALIDITY_RE.test(text);
		const first = scaleMatches[0];
		const firstPos = first.index ?? 0;

		if (!hasReliability) {
			issues.push({
				id: `struct-psych-rel-${issues.length + 1}`,
				code: 'struct.psychometric.reliability',
				category: 'structure',
				severity: 'warning',
				title: 'Ölçme aracı için güvenirlik bilgisi yok',
				detail:
					'Makale bir ölçek veya anket kullanıyor ancak güvenirlik bilgisi (ör. Cronbach α, McDonald ω, ICC) raporlanmamış.',
				quote: excerpt(text.slice(firstPos, firstPos + first[0].length), 220),
				suggestion: 'Kullanılan her ölçme aracı için güvenirlik katsayısını raporlayın.',
				location: index.locationAt(firstPos, firstPos + first[0].length),
				confidence: 'medium',
				decisive: true
			});
		}

		if (!hasValidity) {
			issues.push({
				id: `struct-psych-val-${issues.length + 1}`,
				code: 'struct.psychometric.validity',
				category: 'structure',
				severity: 'info',
				title: 'Ölçme aracı için geçerlik bilgisi yok',
				detail:
					'Makale bir ölçek veya anket kullanıyor ancak geçerlik bilgisinden (ör. faktör analizi, yapı geçerliği) bahsedilmemiş.',
				suggestion: 'Geçerlik kanıtlarını raporlamayı düşünün (ör. DFA, yakınsak geçerlik, AVE).',
				location: {},
				confidence: 'medium',
				decisive: false
			});
		}
	}

	/* Skor ---------------------------------------------------------------- */

	let score = 100;
	score -= missingSections.length * (missingSections.includes('method') ? 18 : 10);
	score -= Math.min(20, unreferencedTables.length * 5 + unreferencedFigures.length * 5);
	score -= danglingRefs.length * 4;
	score -= numberingGaps.length * 2;
	if (order('results') >= 0 && order('method') >= 0 && order('results') < order('method')) score -= 6;

	return {
		result: {
			sections,
			tables,
			figures,
			tableRefs,
			figureRefs,
			missingSections,
			unreferencedTables,
			unreferencedFigures,
			danglingRefs,
			numberingGaps,
			summary: {
				sectionsFound: sections.length,
				tables: tables.length,
				figures: figures.length,
				missing: missingSections.length,
				unreferenced: unreferencedTables.length + unreferencedFigures.length
			},
			score: clamp(score)
		},
		issues
	};
}

function headingConfidence(raw: string, candidate: string, isFirst: boolean): Confidence {
	// Numaralı, kısa ve tek satır başlıklar daha güvenilir
	let c: Confidence = 'medium';
	if (/^\d+(?:\.\d+)*[.)]?\s+/.test(raw.trim())) c = 'high';
	if (/^[A-ZÇĞİÖŞÜ\s]+$/.test(candidate.trim()) && candidate.trim().length < 40) c = 'high';
	if (candidate.length > 60) c = 'low';
	if (isFirst) c = c === 'low' ? 'low' : 'medium';
	return c;
}

/* -------------------------------------------------------------------------- */
/* Altyazılar                                                                  */
/* -------------------------------------------------------------------------- */

const NUM_WORD = String.raw`(\d{1,2})`;

/** Altyazı satırı: "Tablo 1: ...", "Table 2. ...", "Şekil 3 – ..." */
const CAPTION_LINE_RE = new RegExp(
	String.raw`^[ \t]*(?:Tablo|Table|Çizelge|Şekil|Sekil|Figure|Fig\.)\s*\d{1,2}\s*[:.–—-]`,
	'iu'
);

function findCaptions(text: string, type: 'table' | 'figure', index: DocumentIndex): CaptionRef[] {
	const words =
		type === 'table'
			? String.raw`(?:Tablo|Table|TABLO|TABLE|Çizelge)`
			: String.raw`(?:Şekil|Sekil|Figure|FIGURE|Fig\.|Grafik)`;

	// Satır başında "Tablo 1:" veya "Table 1." biçimi; ayırıcı zorunlu,
	// böylece "Tablo 1'de ..." gibi metin içi atıflar altyazı sayılmaz.
	const re = new RegExp(
		String.raw`^[ \t]*${words}\s*${NUM_WORD}\s*[:.–—-]\s*(.{0,200})`,
		'gmu'
	);
	const out: CaptionRef[] = [];
	let m: RegExpExecArray | null;
	while ((m = re.exec(text))) {
		const raw = (m[0] ?? '').replace(/\s+/g, ' ').trim();
		const number = Number(m[1]);
		out.push({
			id: `${type}-${m.index}`,
			type,
			number: Number.isFinite(number) ? number : undefined,
			raw,
			location: index.locationAt(m.index, m.index + raw.length),
			confidence: 'high'
		});
	}
	return out;
}

function findInTextRefs(text: string, type: 'table' | 'figure', index: DocumentIndex): InTextRef[] {
	const words =
		type === 'table'
			? String.raw`(?:Tablo|Table|Çizelge)`
			: String.raw`(?:Şekil|Sekil|Figure|Fig\.)`;

	// "Tablo 1", "Tablo 1 ve 2", "Tablolar 1–3", "Table 2a"
	const re = new RegExp(
		String.raw`(?<![\p{L}])${words}(?:lar|ler|s)?\s*(\d{1,2})(?:\s*(?:[-–—]|ve|and|,)\s*(\d{1,2}))?(?:\s*[a-zçğıöşü])?`,
		'gu'
	);
	const out: InTextRef[] = [];
	let m: RegExpExecArray | null;
	while ((m = re.exec(text))) {
		const raw = m[0].replace(/\s+/g, ' ').trim();

		// Altyazı satırlarını metin içi referans sayma
		const lineStart = text.lastIndexOf('\n', m.index) + 1;
		const lineEnd = text.indexOf('\n', m.index);
		const line = text.slice(lineStart, lineEnd === -1 ? text.length : lineEnd);
		if (CAPTION_LINE_RE.test(line)) continue;

		const nums = [m[1], m[2]].filter(Boolean).map(Number);
		for (const n of nums) {
			out.push({
				id: `${type}-ref-${m.index}-${n}`,
				type,
				number: n,
				raw,
				location: index.locationAt(m.index, m.index + raw.length),
				confidence: 'high'
			});
		}
	}
	return out;
}

function clamp(v: number): number {
	return Math.max(0, Math.min(100, Math.round(v)));
}
